import {
  ImageScaleView,
  ImageScaleViewOpts,
  Transition,
} from '@play-co/timestep-core/ui';
import { clamp } from 'src/replicant/utils';

export abstract class GenericProgressBar extends ImageScaleView {
  /**
   * Current progress. Will be based on the domain.
   */
  public get progress(): number {
    return this._progress;
  }
  /**
   * Set progress based on the domain.
   */
  public set progress(value: number) {
    this._progress = value;

    this.emit('progress', value);
  }
  protected _progress: number = 0;

  /**
   * Get current domain.
   */
  public get domain(): number[] {
    return this._domain;
  }
  /**
   * Set current domain.
   * The default domain is [0, 1].
   * It's also possible to define more than two items, making the progress non-linear.
   */
  public set domain(value: number[]) {
    this._domain = value;
    this.progress = this._progress;
  }
  protected _domain: number[] = [0, 1];

  /**
   * Aniamte current progress to the given value.
   * @param value - Target value.
   * @param duration - Animation duration.
   * @param easing - Animation easing.
   */
  public abstract animateProgress(
    value: number,
    duration: number,
    easing: Transition,
  ): Promise<void>;

  /**
   * Normalize value based on domain.
   * @param value - Target value.
   * @returns
   */
  public getNormalizedProgress(value: number): number {
    // Get last milestone.
    const lastMilestone = this.domain.length - 1;

    // Clamp value.
    value = Math.max(0, Math.min(value, this.domain[lastMilestone]));

    // Get data of current milestone.
    const currentMilestone = this.domain.findIndex((x) => value <= x);
    const currentMilestoneValue = this.domain[currentMilestone];

    // Get data of previous milestone.
    const previousMilestone = Math.max(currentMilestone - 1, 0);
    const previousMilestoneProgress = previousMilestone / lastMilestone;
    const previousMilestoneValue = this.domain[previousMilestone];

    // Normalize the progress, based on the current milestone.
    const normalizedMilestoneProgress =
      (value - previousMilestoneValue) /
      Math.max(currentMilestoneValue - previousMilestoneValue, 1);

    //  Calculate segment size.
    const segmentSize = 1 / lastMilestone;

    // Get progress to current milestone.
    const currentMilestoneProgress = normalizedMilestoneProgress * segmentSize;

    // The progress will be the accumulated progress plus the progress to the next milestone.
    const currentProgress =
      previousMilestoneProgress + currentMilestoneProgress;

    return clamp(currentProgress, 0, 1);
  }
}
